import * as i0 from '@angular/core';
import { inject, ElementRef, Injector, IterableDiffers, Component, Input, ViewChild, HostBinding, HostListener, NgModule } from '@angular/core';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i9 from '@angular/cdk/scrolling';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { AsyncPipe, NgIf } from '@angular/common';
import * as i1 from '@angular/forms';
import { UntypedFormControl, NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i2 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i3 from '@angular/material/checkbox';
import { MatCheckboxModule } from '@angular/material/checkbox';
import * as i4 from '@angular/material/form-field';
import { MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import * as i5 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i6 from '@angular/material/input';
import { MatInput, MatInputModule } from '@angular/material/input';
import * as i7 from '@angular/material/select';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { _findObjWithKey, _isObject, _doesContainOnlyStrings, _removeIntersection } from '@fabric/components/private/utilities';
import { Subject } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import * as i8 from '@angular/material/core';

/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
class FabMultiselectComponent {
    /** self generating ids which starts from zero*/
    static { this.nextId = 0; }
    get disabled() {
        return this._disabled;
    }
    set disabled(disabled) {
        this._disabled = coerceBooleanProperty(disabled);
        this.stateChanges.next();
    }
    get placeholder() {
        return this._placeholder;
    }
    set placeholder(placeholder) {
        this._placeholder = placeholder;
        this.stateChanges.next();
    }
    get required() {
        return this._required;
    }
    set required(required) {
        this._required = coerceBooleanProperty(required);
        this.stateChanges.next();
    }
    get value() {
        return this.val;
    }
    set value(val) {
        this.val = val;
        this.selectedMatOptions = val;
        this.matSelectFormControl.setValue(val);
        this.triggerValue = new Set(val);
        this._onChange(val);
        this._onTouched();
        this.stateChanges.next();
    }
    constructor() {
        /** input for the multi select*/
        this.options = [];
        /** Displays the field that can be viewed by the users as selectable items. */
        this.labelField = 'label';
        /** The values assigned for the multi select */
        this.valueField = 'value';
        /** Host Bindings */
        this.describedBy = '';
        this.componentId = '';
        this.controlType = 'fabMultiselect';
        this.focused = false;
        this.errorState = false;
        this.inputFormControl = new UntypedFormControl();
        this.matSelectFormControl = new UntypedFormControl();
        this.stateChanges = new Subject();
        /** disabled state for control */
        this._disabled = false;
        /** required state for control */
        this._required = false;
        /** find object within an array based on a key */
        this.findObjWithKey = _findObjWithKey;
        /** isObject to verify if item is an object */
        this.isObject = _isObject;
        /** panel height for the list popup. Updates based on the list size */
        this.panelHeight = 0;
        /** select all defaulted to false */
        this.selectAllCheck = false;
        /** trigger value as a Set, assigned to display count of items selected */
        this.triggerValue = new Set();
        /** selected values */
        this.selectedMatOptions = [];
        /** filtered data after options have been filtered */
        this.filteredDataArray = [];
        /** boolean that tracks if multiselect is expanded */
        this.isExpanded = false;
        /** id for the dropdown element */
        this.dropdownId = '';
        this._elementRef = inject(ElementRef);
        this.injector = inject(Injector);
        this.focusMonitor = inject(FocusMonitor);
        this._onChange = (val) => {
            this.val = val;
        };
        this._onTouched = () => { };
        this.focusMonitor.monitor(this._elementRef.nativeElement, true).subscribe((origin) => {
            this.focused = !!origin;
            this.stateChanges.next();
        });
        const iterableDiffers = inject(IterableDiffers);
        this.optionsDiffer = iterableDiffers.find(this.options).create(null);
    }
    get shouldLabelFloat() {
        return !this.empty;
    }
    get empty() {
        return !!this.value && this.value.length === 0;
    }
    /**
     * Set initial component state
     */
    ngOnInit() {
        this.ngControl = this.injector.get(NgControl);
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }
        // add a computed id to the element if it is not provided by the user.
        FabMultiselectComponent.nextId++;
        this.componentId = this.id ? this.id : `fab-multiselect-${FabMultiselectComponent.nextId}`;
        this.dropdownId = `fab-multiselect-dropdown-${FabMultiselectComponent.nextId}`;
        // If the initial options array is empty, then disable the component
        // ngDoCheck will later enable it, when options are populated
        if (!this.options?.length) {
            this.matSelectFormControl.disable();
        }
    }
    /**
     * Update internal state when the options array has changed
     */
    ngDoCheck() {
        if (this.ngControl) {
            this.errorState = this.ngControl.invalid && this.ngControl.touched;
            this.stateChanges.next();
        }
        // Check if the options array has changed
        const optionsChanges = this.optionsDiffer.diff(this.options);
        if (optionsChanges) {
            // If the options for the multiselect is not defined,
            // assign an empty array, so it does not throw an error
            // validating the options for objects or strings.
            if (!this.options) {
                this.options = [];
            }
            // If no options are in the list, then we disable the component to mimic mat-select behavior
            // When options are provided, we re-enable.
            if (!this.options.length) {
                this.matSelectFormControl.disable();
            }
            else {
                this.matSelectFormControl.enable();
            }
            if (_isObject(this.options[0])) {
                if (this.options[0].hasOwnProperty(this.labelField) && this.options[0].hasOwnProperty(this.valueField)) {
                    this.filterData();
                }
                else {
                    throw new Error('Object does not match "label" and "value" property.');
                }
            }
            else if (_doesContainOnlyStrings(this.options)) {
                this.filterData();
            }
            else {
                throw new Error('Error in data structure, only any[] and string[] is allowed.');
            }
            this.subscribeToFilterData();
        }
    }
    /**
     * Unsubscribe from focus monitor and filter subscriptions
     */
    ngOnDestroy() {
        this.stateChanges.complete();
        this.focusMonitor.stopMonitoring(this._elementRef.nativeElement);
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
    }
    /**
     * Respond to container click event
     * @param event - Mouse event corresponding to the click event
     */
    onContainerClick(event) {
        this.matSelect.open();
        if (event.target.tagName.toLowerCase() !== 'div') {
            this.container.nativeElement.querySelector('div').focus();
        }
    }
    /**
     * Register the `onChange` event for the control
     * @param fn - function to be called when the control is changed
     */
    registerOnChange(fn) {
        this._onChange = fn;
    }
    /**
     * Register the `onTouched` event for the control
     * @param fn - function to be called when the control is touched
     */
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    /**
     * Sets the disabled state through the reactive form control
     * @param isDisabled - disable argument provided through the form control
     */
    setDisabledState(isDisabled) {
        if (isDisabled) {
            this._disabled = coerceBooleanProperty(isDisabled);
            this.stateChanges.next();
            this.matSelectFormControl.disable();
            this.inputFormControl.disable();
        }
        else {
            this.matSelectFormControl.enable();
            this.inputFormControl.enable();
        }
    }
    /**
     * Sets the described by ids for the mat select
     */
    setDescribedByIds() {
        // ids string[] is not added, reason being angular was setting empty IDs for second element
        // this way describedBy can be set using the custom ID or provided by the component
        this.describedBy = this.id ? this.id : this.componentId;
    }
    /**
     * Write the value into mat select
     * takes in an array of [valueField] to preselect content or
     * takes in array of string to preselect content
     * @param obj - items to preselect in mat select
     */
    writeValue(obj) {
        // If obj and options have value, write the value
        // If obj and options don't have value, set the select to empty array
        if (obj && this.options.length !== 0) {
            this.value = obj;
        }
        else {
            this.value = [];
        }
    }
    /**
     * open and close events for the mat select panel
     * @param event - boolean
     */
    openChange(event) {
        if (!event) {
            this.cdkVirtualScrollViewport.scrollToIndex(0);
            this.cdkVirtualScrollViewport.checkViewportSize();
        }
        this.isExpanded = event;
        this.matInput.focus();
        this.inputFormControl.setValue('');
        this.matSelectFormControl.setValue(this.selectedMatOptions);
        this.selectAllSelectionCheck();
    }
    /**
     * action for the select all check box
     * select and deselect all the contents inside mat select
     */
    selectAllChange() {
        this.selectAllCheck = !this.selectAllCheck; // to control select-unselect
        if (this.selectAllCheck) {
            this.selectedMatOptions = this.selectedMatOptions.concat(this.filteredDataArray);
            this.matSelectFormControl.setValue(this.selectedMatOptions);
            this.writeValue(Array.from(new Set(this.selectedMatOptions)));
            this.triggerValue = new Set(this.selectedMatOptions);
        }
        else {
            if (this.inputFormControl.value) {
                this.selectedMatOptions = _removeIntersection(this.selectedMatOptions, this.filteredDataArray);
                this.matSelectFormControl.setValue(this.selectedMatOptions);
                this.writeValue(this.selectedMatOptions);
                this.triggerValue = new Set(this.selectedMatOptions);
            }
            else {
                this.selectedMatOptions.push([]);
                this.matSelectFormControl.setValue([]);
                this.writeValue([]);
                this.triggerValue = new Set([]);
            }
        }
    }
    /**
     * selection change event thrown during check / uncheck of mat select options
     * @param event - Mat select change event
     */
    selectionChange(event) {
        event.source.options.map((result) => {
            if (result.selected) {
                this.selectedMatOptions.push(result.value);
            }
            else {
                this.selectedMatOptions = this.selectedMatOptions.filter((item) => item !== result.value);
            }
        });
        // write value to the component ngControl
        this.writeValue(Array.from(new Set(this.selectedMatOptions)));
        // check for the select all condition
        this.selectAllSelectionCheck();
        // trigger value for the component text
        this.triggerValue = new Set(this.selectedMatOptions);
    }
    /**
     * checks for the condition when all the items in the mat select is checked
     */
    selectAllSelectionCheck() {
        const checker = (arr, target) => target.every((item) => arr.includes(item));
        this.selectAllCheck = checker(this.selectedMatOptions, this.filteredDataArray);
    }
    /**
     * function to filter similar contents from an array
     * @param value - value to be filtered from an array
     * @returns filtered array with contents that matches the value
     */
    _filter(value) {
        /* set the value of the mat select control*/
        this.matSelectFormControl.setValue(this.selectedMatOptions);
        /* filter based on the input field*/
        const filterValue = value.toLowerCase();
        if (_isObject(this.options[0])) {
            if (this.options[0].hasOwnProperty(this.labelField) && this.options[0].hasOwnProperty(this.valueField)) {
                return this.options.filter((item) => item[this.labelField].toLowerCase().includes(filterValue));
            }
            else {
                return undefined;
            }
        }
        else {
            return this.options.filter((item) => item.toLowerCase().includes(filterValue));
        }
    }
    /**
     * filters data based on current input and stores them in an Observable
     */
    filterData() {
        this.filteredData = this.inputFormControl.valueChanges.pipe(startWith(null), map((item) => (item ? this._filter(item) : this.options.slice())));
    }
    /**
     * subscribe to filtered data
     */
    subscribeToFilterData() {
        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
        const fullPanelHeight = 14.2;
        const panelHeightForLessThanFive = 2.86;
        this.filterSubscription = this.filteredData.subscribe((res) => {
            this.filteredDataArray = [];
            if (_isObject(this.options[0])) {
                res.forEach((item) => this.filteredDataArray.push(item[this.valueField]));
            }
            else {
                this.filteredDataArray = res;
            }
            if (this.filteredDataArray.length <= 5 && this.filteredDataArray.length > 0) {
                this.panelHeight = this.filteredDataArray.length * panelHeightForLessThanFive;
            }
            else if (this.filteredDataArray.length === 0) {
                this.panelHeight = panelHeightForLessThanFive;
            }
            else {
                this.panelHeight = fullPanelHeight;
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabMultiselectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.7", type: FabMultiselectComponent, isStandalone: true, selector: "fab-multiselect", inputs: { id: "id", options: "options", labelField: "labelField", valueField: "valueField", disabled: "disabled", placeholder: "placeholder", required: "required", value: "value" }, host: { listeners: { "change": "_onChange()", "blur": "_onTouched()" }, properties: { "attr.aria-describedby": "this.describedBy", "class.floating": "this.componentId", "attr.id": "this.componentId" } }, providers: [
            {
                provide: MatFormFieldControl,
                useExisting: FabMultiselectComponent,
            },
        ], viewQueries: [{ propertyName: "container", first: true, predicate: ["fabOptionSelectContainer"], descendants: true, read: ElementRef }, { propertyName: "fabOptionSelect", first: true, predicate: ["fabOptionSelect"], descendants: true, read: ElementRef }, { propertyName: "matSelect", first: true, predicate: MatSelect, descendants: true }, { propertyName: "matInput", first: true, predicate: MatInput, descendants: true }, { propertyName: "cdkVirtualScrollViewport", first: true, predicate: CdkVirtualScrollViewport, descendants: true }], ngImport: i0, template: "<!--\n  ~ Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.\n  ~ This information is confidential and proprietary to GM Global Technology\n  ~ Operations LLC and may not be used, modified, copied or distributed.\n  -->\n<div #fabOptionSelectContainer id=\"fab-multi-select-container\" class=\"fab-multi-select-container\">\n  <mat-select\n    #fabOptionSelect\n    id=\"fabOptionSelect\"\n    aria-label=\"Multi Select\"\n    [attr.aria-expanded]=\"isExpanded\"\n    aria-controls=\"options-dropdown\"\n    [class.state-disabled]=\"_disabled\"\n    disableOptionCentering=\"true\"\n    [formControl]=\"matSelectFormControl\"\n    (selectionChange)=\"selectionChange($event)\"\n    (openedChange)=\"openChange($event)\"\n    multiple\n  >\n    <mat-form-field id=\"fabOptionSelectFilterInput\" class=\"w-100\">\n      <mat-checkbox\n        matIconPrefix\n        (ngModelChange)=\"selectAllChange()\"\n        [(ngModel)]=\"selectAllCheck\"\n        color=\"primary\"\n        class=\"fab-select-all-check-box\"\n        [disabled]=\"(filteredData | async)?.length <= 0\"\n      />\n      <input\n        matInput\n        (ngModelChange)=\"selectAllSelectionCheck()\"\n        [formControl]=\"inputFormControl\"\n        placeholder=\"Filter ...\"\n        autocomplete=\"off\"\n      />\n      <button\n        mat-icon-button\n        class=\"clear-filter-btn\"\n        *ngIf=\"inputFormControl.value\"\n        matSuffix\n        aria-label=\"Clear\"\n        (click)=\"inputFormControl.reset(); selectAllSelectionCheck()\"\n      >\n        <mat-icon>close</mat-icon>\n      </button>\n    </mat-form-field>\n    <mat-select-trigger />\n    <div class=\"fab-select-options\" [id]=\"dropdownId\">\n      <cdk-virtual-scroll-viewport\n        [itemSize]=\"40\"\n        [style.height.em]=\"panelHeight\"\n        [minBufferPx]=\"250\"\n        [maxBufferPx]=\"600\"\n        appendOnly\n        class=\"w-100\"\n      >\n        <span *ngIf=\"!isObject(options[0])\">\n          <mat-option [style.height.px]=\"40\" *cdkVirtualFor=\"let item of filteredData | async\" [value]=\"item\">{{\n            item\n          }}</mat-option>\n        </span>\n        <span *ngIf=\"isObject(options[0])\">\n          <mat-option\n            [style.height.px]=\"40\"\n            *cdkVirtualFor=\"let item of filteredData | async\"\n            [value]=\"item[valueField]\"\n            >{{ item[labelField] }}</mat-option\n          >\n        </span>\n        <mat-option [style.height.px]=\"40\" *ngIf=\"(filteredData | async)?.length <= 0\" disabled\n          >No matches found</mat-option\n        >\n      </cdk-virtual-scroll-viewport>\n    </div>\n  </mat-select>\n  <div class=\"selected-text\">\n    <span *ngIf=\"!isObject(options[0])\">{{ matSelectFormControl.value ? matSelectFormControl.value[0] : '' }}</span>\n    <span *ngIf=\"isObject(options[0])\">{{\n      matSelectFormControl?.value?.length !== 0\n        ? findObjWithKey(options, valueField, matSelectFormControl?.value[0])[labelField]\n        : ''\n    }}</span>\n    <span *ngIf=\"triggerValue?.size > 1\" class=\"additional-selection\">\n      (+{{ triggerValue?.size - 1 }} {{ triggerValue?.size === 2 ? 'other' : 'others' }})\n    </span>\n  </div>\n</div>\n", styles: ["/*!\n * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.\n * This information is confidential and proprietary to GM Global Technology\n * Operations LLC and may not be used, modified, copied or distributed.\n *//*!\n * Copyright (c) 2022 GM Global Technology Operations LLC. All rights reserved.\n * This information is confidential and proprietary to GM Global Technology\n * Operations LLC and may not be used, modified, copied or distributed.\n *//*!\n * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.\n * This information is confidential and proprietary to GM Global Technology\n * Operations LLC and may not be used, modified, copied or distributed.\n */::ng-deep #fabOptionSelectFilterInput .mat-mdc-form-field-flex,::ng-deep #fabOptionSelectFilterInput .mat-form-field-flex{border-bottom:.1em solid #959593;padding-top:0}.fab-select-all-check-box{margin-left:6px}.fab-select-options{overflow:auto;margin:-16px 0 0}.fab-multi-select-container{font-size:14px;display:flex!important}.fab-multi-select-container .state-disabled{pointer-events:none}::ng-deep #fabOptionSelect-panel{overflow:unset!important}::ng-deep #fabOptionSelect-panel .cdk-virtual-scroll-content-wrapper{width:inherit}::ng-deep #fabOptionSelect{display:flex!important}::ng-deep #fabOptionSelect .mat-mdc-select-trigger,::ng-deep #fabOptionSelect .mat-select-trigger{width:100%!important}.selected-text{display:flex;position:absolute;cursor:pointer}.selected-text .additional-selection{opacity:.75;font-size:.75em;display:flex;align-items:center;padding-left:5px}.clear-filter-btn{margin-right:4px}\n"], dependencies: [{ kind: "pipe", type: AsyncPipe, name: "async" }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "ngmodule", type: MatButtonModule }, { kind: "component", type: i2.MatIconButton, selector: "button[mat-icon-button]", inputs: ["disabled", "disableRipple", "color"], exportAs: ["matButton"] }, { kind: "ngmodule", type: MatCheckboxModule }, { kind: "component", type: i3.MatCheckbox, selector: "mat-checkbox", inputs: ["disableRipple", "color", "tabIndex"], exportAs: ["matCheckbox"] }, { kind: "ngmodule", type: MatFormFieldModule }, { kind: "component", type: i4.MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: i4.MatPrefix, selector: "[matPrefix], [matIconPrefix], [matTextPrefix]", inputs: ["matTextPrefix"] }, { kind: "directive", type: i4.MatSuffix, selector: "[matSuffix], [matIconSuffix], [matTextSuffix]", inputs: ["matTextSuffix"] }, { kind: "ngmodule", type: MatIconModule }, { kind: "component", type: i5.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "ngmodule", type: MatInputModule }, { kind: "directive", type: i6.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "ngmodule", type: MatSelectModule }, { kind: "component", type: i7.MatSelect, selector: "mat-select", inputs: ["disabled", "disableRipple", "tabIndex", "panelWidth", "hideSingleSelectionIndicator"], exportAs: ["matSelect"] }, { kind: "directive", type: i7.MatSelectTrigger, selector: "mat-select-trigger" }, { kind: "component", type: i8.MatOption, selector: "mat-option", exportAs: ["matOption"] }, { kind: "directive", type: NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "ngmodule", type: ScrollingModule }, { kind: "directive", type: i9.CdkFixedSizeVirtualScroll, selector: "cdk-virtual-scroll-viewport[itemSize]", inputs: ["itemSize", "minBufferPx", "maxBufferPx"] }, { kind: "directive", type: i9.CdkVirtualForOf, selector: "[cdkVirtualFor][cdkVirtualForOf]", inputs: ["cdkVirtualForOf", "cdkVirtualForTrackBy", "cdkVirtualForTemplate", "cdkVirtualForTemplateCacheSize"] }, { kind: "component", type: i9.CdkVirtualScrollViewport, selector: "cdk-virtual-scroll-viewport", inputs: ["orientation", "appendOnly"], outputs: ["scrolledIndexChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabMultiselectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fab-multiselect', standalone: true, imports: [
                        AsyncPipe,
                        FormsModule,
                        MatButtonModule,
                        MatCheckboxModule,
                        MatFormFieldModule,
                        MatIconModule,
                        MatInputModule,
                        MatSelectModule,
                        NgIf,
                        ReactiveFormsModule,
                        ScrollingModule,
                    ], providers: [
                        {
                            provide: MatFormFieldControl,
                            useExisting: FabMultiselectComponent,
                        },
                    ], template: "<!--\n  ~ Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.\n  ~ This information is confidential and proprietary to GM Global Technology\n  ~ Operations LLC and may not be used, modified, copied or distributed.\n  -->\n<div #fabOptionSelectContainer id=\"fab-multi-select-container\" class=\"fab-multi-select-container\">\n  <mat-select\n    #fabOptionSelect\n    id=\"fabOptionSelect\"\n    aria-label=\"Multi Select\"\n    [attr.aria-expanded]=\"isExpanded\"\n    aria-controls=\"options-dropdown\"\n    [class.state-disabled]=\"_disabled\"\n    disableOptionCentering=\"true\"\n    [formControl]=\"matSelectFormControl\"\n    (selectionChange)=\"selectionChange($event)\"\n    (openedChange)=\"openChange($event)\"\n    multiple\n  >\n    <mat-form-field id=\"fabOptionSelectFilterInput\" class=\"w-100\">\n      <mat-checkbox\n        matIconPrefix\n        (ngModelChange)=\"selectAllChange()\"\n        [(ngModel)]=\"selectAllCheck\"\n        color=\"primary\"\n        class=\"fab-select-all-check-box\"\n        [disabled]=\"(filteredData | async)?.length <= 0\"\n      />\n      <input\n        matInput\n        (ngModelChange)=\"selectAllSelectionCheck()\"\n        [formControl]=\"inputFormControl\"\n        placeholder=\"Filter ...\"\n        autocomplete=\"off\"\n      />\n      <button\n        mat-icon-button\n        class=\"clear-filter-btn\"\n        *ngIf=\"inputFormControl.value\"\n        matSuffix\n        aria-label=\"Clear\"\n        (click)=\"inputFormControl.reset(); selectAllSelectionCheck()\"\n      >\n        <mat-icon>close</mat-icon>\n      </button>\n    </mat-form-field>\n    <mat-select-trigger />\n    <div class=\"fab-select-options\" [id]=\"dropdownId\">\n      <cdk-virtual-scroll-viewport\n        [itemSize]=\"40\"\n        [style.height.em]=\"panelHeight\"\n        [minBufferPx]=\"250\"\n        [maxBufferPx]=\"600\"\n        appendOnly\n        class=\"w-100\"\n      >\n        <span *ngIf=\"!isObject(options[0])\">\n          <mat-option [style.height.px]=\"40\" *cdkVirtualFor=\"let item of filteredData | async\" [value]=\"item\">{{\n            item\n          }}</mat-option>\n        </span>\n        <span *ngIf=\"isObject(options[0])\">\n          <mat-option\n            [style.height.px]=\"40\"\n            *cdkVirtualFor=\"let item of filteredData | async\"\n            [value]=\"item[valueField]\"\n            >{{ item[labelField] }}</mat-option\n          >\n        </span>\n        <mat-option [style.height.px]=\"40\" *ngIf=\"(filteredData | async)?.length <= 0\" disabled\n          >No matches found</mat-option\n        >\n      </cdk-virtual-scroll-viewport>\n    </div>\n  </mat-select>\n  <div class=\"selected-text\">\n    <span *ngIf=\"!isObject(options[0])\">{{ matSelectFormControl.value ? matSelectFormControl.value[0] : '' }}</span>\n    <span *ngIf=\"isObject(options[0])\">{{\n      matSelectFormControl?.value?.length !== 0\n        ? findObjWithKey(options, valueField, matSelectFormControl?.value[0])[labelField]\n        : ''\n    }}</span>\n    <span *ngIf=\"triggerValue?.size > 1\" class=\"additional-selection\">\n      (+{{ triggerValue?.size - 1 }} {{ triggerValue?.size === 2 ? 'other' : 'others' }})\n    </span>\n  </div>\n</div>\n", styles: ["/*!\n * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.\n * This information is confidential and proprietary to GM Global Technology\n * Operations LLC and may not be used, modified, copied or distributed.\n *//*!\n * Copyright (c) 2022 GM Global Technology Operations LLC. All rights reserved.\n * This information is confidential and proprietary to GM Global Technology\n * Operations LLC and may not be used, modified, copied or distributed.\n *//*!\n * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.\n * This information is confidential and proprietary to GM Global Technology\n * Operations LLC and may not be used, modified, copied or distributed.\n */::ng-deep #fabOptionSelectFilterInput .mat-mdc-form-field-flex,::ng-deep #fabOptionSelectFilterInput .mat-form-field-flex{border-bottom:.1em solid #959593;padding-top:0}.fab-select-all-check-box{margin-left:6px}.fab-select-options{overflow:auto;margin:-16px 0 0}.fab-multi-select-container{font-size:14px;display:flex!important}.fab-multi-select-container .state-disabled{pointer-events:none}::ng-deep #fabOptionSelect-panel{overflow:unset!important}::ng-deep #fabOptionSelect-panel .cdk-virtual-scroll-content-wrapper{width:inherit}::ng-deep #fabOptionSelect{display:flex!important}::ng-deep #fabOptionSelect .mat-mdc-select-trigger,::ng-deep #fabOptionSelect .mat-select-trigger{width:100%!important}.selected-text{display:flex;position:absolute;cursor:pointer}.selected-text .additional-selection{opacity:.75;font-size:.75em;display:flex;align-items:center;padding-left:5px}.clear-filter-btn{margin-right:4px}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { id: [{
                type: Input
            }], options: [{
                type: Input
            }], labelField: [{
                type: Input
            }], valueField: [{
                type: Input
            }], disabled: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], required: [{
                type: Input
            }], value: [{
                type: Input
            }], container: [{
                type: ViewChild,
                args: ['fabOptionSelectContainer', { static: false, read: ElementRef }]
            }], fabOptionSelect: [{
                type: ViewChild,
                args: ['fabOptionSelect', { static: false, read: ElementRef }]
            }], matSelect: [{
                type: ViewChild,
                args: [MatSelect, { static: false }]
            }], matInput: [{
                type: ViewChild,
                args: [MatInput, { static: false }]
            }], cdkVirtualScrollViewport: [{
                type: ViewChild,
                args: [CdkVirtualScrollViewport]
            }], describedBy: [{
                type: HostBinding,
                args: ['attr.aria-describedby']
            }], componentId: [{
                type: HostBinding,
                args: ['class.floating']
            }, {
                type: HostBinding,
                args: ['attr.id']
            }], _onChange: [{
                type: HostListener,
                args: ['change']
            }], _onTouched: [{
                type: HostListener,
                args: ['blur']
            }] } });

/*
 * Copyright (c) 2022-2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * `FabMultiselectComponent` is now standalone.  You can import the component directly.
 * `NgModule` left in place for backwards compatibility.
 */
class FabMultiselectModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabMultiselectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.7", ngImport: i0, type: FabMultiselectModule, imports: [FabMultiselectComponent], exports: [FabMultiselectComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabMultiselectModule, imports: [FabMultiselectComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.7", ngImport: i0, type: FabMultiselectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [FabMultiselectComponent],
                    exports: [FabMultiselectComponent],
                }]
        }] });

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Created by samit purkait 06/29/2020.
 */

/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
/**
 * Created by samit purkait on 06/29/2020.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { FabMultiselectComponent, FabMultiselectModule };
